import { useState, useEffect, useRef } from "react";
import { Box, IconButton, Tooltip, Typography, Button, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Assessment as RiskIcon,
  Info as ClauseIcon,
  Lightbulb as SuggestionIcon,
  Group as CollabIcon,
  History as HistoryIcon,
  LockOutlined as LockIcon,
} from "@mui/icons-material";
import DrawIcon from '@mui/icons-material/Draw';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FindInPageOutlinedIcon from '@mui/icons-material/FindInPageOutlined';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import RiskAnalysis from "./RiskAnalysis/RiskAnalysis";
import Explanation from "./Explanation/Explanation";
import Collaboration from "./Collaborator/Collaboration";
import Suggestion from "./Suggestion/Suggestion";
import Signature from "./Signature/Signature";
import ChatBot from "./ChatBot/ChatBot";
import HistoryComponent from "./History/History";
import KeyInfo from "./KeyInfo/KeyInfo";
import DownloadDialog from "../dialogs/DownloadDialog";
import ReactGA from 'react-ga4';
import { tracker } from '../../../analytics';
import eventSourceManager from '../../../utils/eventSourceManager';
import { getRiskAnalysis, getClauses, getSuggestedClauses, riskAnalysisComplete, clauseComplete, SuggestedClauseComplete, extractKeyInfo, keyInfoExtractComplete } from '../../../redux/actions/contractAction';

const TABS = [
  { id: "chatBot",      icon: <AutoAwesomeIcon sx={{ fontSize: 18 }} />, labelKey: "rightPanel.tabConversation" },
  { id: "keyInfo",      icon: <FindInPageOutlinedIcon sx={{ fontSize: 18 }} />, labelKey: "rightPanel.tabKeyInfo" },
  { id: "riskAnalysis", icon: <RiskIcon sx={{ fontSize: 18 }} />,        labelKey: "rightPanel.tabRiskAnalysis" },
  { id: "explanation",  icon: <ClauseIcon sx={{ fontSize: 18 }} />,      labelKey: "rightPanel.tabClauses" },
  { id: "suggestion",   icon: <SuggestionIcon sx={{ fontSize: 18 }} />,  labelKey: "rightPanel.tabSuggestions" },
  { id: "collaboration",icon: <CollabIcon sx={{ fontSize: 18 }} />,      labelKey: "rightPanel.tabCollaboration" },
  { id: "signature",    icon: <DrawIcon sx={{ fontSize: 18 }} />,        labelKey: "rightPanel.tabSignature" },
  { id: "history",      icon: <HistoryIcon sx={{ fontSize: 18 }} />,     labelKey: "rightPanel.tabHistory" },
];

// Fallback labels (used if translation key not found)
const TAB_LABELS = {
  "rightPanel.tabConversation": "Conversation",
  "rightPanel.tabKeyInfo": "Informations clés",
  "rightPanel.tabRiskAnalysis": "Analyse des risques",
  "rightPanel.tabClauses": "Extraction des clauses",
  "rightPanel.tabSuggestions": "Suggestions de clauses",
  "rightPanel.tabCollaboration": "Collaboration",
  "rightPanel.tabSignature": "Signature",
  "rightPanel.tabHistory": "Historique",
};

const LockedTabOverlay = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      px: 3,
      textAlign: 'center',
    }}>
      <LockIcon sx={{
        fontSize: 48,
        color: alpha(theme.palette.text.secondary, 0.2),
        mb: 2,
      }} />
      <Typography variant="body1" sx={{
        color: theme.palette.text.secondary,
        mb: 1,
        fontWeight: 500,
      }}>
        {t("rightPanel.lockedMessage", "Disponible avec votre propre espace Lawis")}
      </Typography>
      <Typography variant="body2" sx={{
        color: alpha(theme.palette.text.secondary, 0.6),
        mb: 3,
        maxWidth: 280,
      }}>
        {t("rightPanel.lockedDescription", "Start your free trial to access AI analysis, clause extraction, and more.")}
      </Typography>
      <Button
        variant="contained"
        onClick={() => navigate('/subscribe')}
        sx={{
          textTransform: 'none',
          borderRadius: '8px',
          px: 3,
          py: 1,
          fontWeight: 600,
          backgroundColor: '#c9a96e',
          color: '#1c1917',
          '&:hover': { backgroundColor: '#b8963f' },
        }}
      >
        {t("rightPanel.startTrial", "Commencer votre essai gratuit")}
      </Button>
    </Box>
  );
};

const RightPanel = ({ contractId, onBeforeDownload, lockedTabs = [] }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState("chatBot");
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const riskConnIdRef = useRef(null);
  const clauseConnIdRef = useRef(null);
  const suggestConnIdRef = useRef(null);
  const keyInfoConnIdRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // If initial tab is locked, default to first unlocked tab
  useEffect(() => {
    if (lockedTabs.includes(activeView)) {
      const firstUnlocked = TABS.find(tab => !lockedTabs.includes(tab.id));
      if (firstUnlocked) setActiveView(firstUnlocked.id);
    }
  }, [lockedTabs]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (riskConnIdRef.current) {
        eventSourceManager.closeConnection(riskConnIdRef.current);
        dispatch(riskAnalysisComplete());
      }
      if (clauseConnIdRef.current) {
        eventSourceManager.closeConnection(clauseConnIdRef.current);
        dispatch(clauseComplete());
      }
      if (suggestConnIdRef.current) {
        eventSourceManager.closeConnection(suggestConnIdRef.current);
        dispatch(SuggestedClauseComplete());
      }
      if (keyInfoConnIdRef.current) {
        eventSourceManager.closeConnection(keyInfoConnIdRef.current);
        dispatch(keyInfoExtractComplete());
      }
    };
  }, [dispatch]);

  const startRiskAnalysis = async () => {
    if (riskConnIdRef.current) eventSourceManager.closeConnection(riskConnIdRef.current);
    const result = await dispatch(getRiskAnalysis(contractId));
    if (result && result.connectionId) riskConnIdRef.current = result.connectionId;
  };
  const startClauseExtraction = async () => {
    if (clauseConnIdRef.current) eventSourceManager.closeConnection(clauseConnIdRef.current);
    const result = await dispatch(getClauses(contractId));
    if (result && result.connectionId) clauseConnIdRef.current = result.connectionId;
  };
  const startSuggestions = async () => {
    if (suggestConnIdRef.current) eventSourceManager.closeConnection(suggestConnIdRef.current);
    const result = await dispatch(getSuggestedClauses(contractId));
    if (result && result.connectionId) suggestConnIdRef.current = result.connectionId;
  };
  const startKeyInfoExtraction = async () => {
    if (keyInfoConnIdRef.current) eventSourceManager.closeConnection(keyInfoConnIdRef.current);
    const result = await dispatch(extractKeyInfo(contractId));
    if (result && result.connectionId) keyInfoConnIdRef.current = result.connectionId;
  };

  const userId = useSelector(state => state.auth.user?.user?.id);

  const handleSetView = (view) => {
    ReactGA.event({ category: 'RightPanel', action: 'Switch View', label: view });
    tracker.event({ category: 'RightPanel', action: 'Switch View', label: view });
    setActiveView(view);
  };

  const isTabLocked = (tabId) => lockedTabs.includes(tabId);

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      px: 1.5,
      pb: 1.5,
      pt: 1.5,
    }}>

      {/* ── Top action bar ── */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        mb: 1.5,
      }}>
        <Box
          component="button"
          onClick={() => setDownloadDialogOpen(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            height: 32,
            px: 1.5,
            borderRadius: '8px',
            border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
            backgroundColor: isDark ? alpha('#fff', 0.05) : '#fff',
            color: theme.palette.text.primary,
            fontFamily: theme.typography.fontFamily,
            fontSize: '0.8rem',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'all 0.15s',
            outline: 'none',
            '&:hover': {
              backgroundColor: isDark ? alpha('#fff', 0.09) : alpha('#000', 0.04),
              borderColor: alpha(theme.palette.text.secondary, 0.4),
            },
          }}
        >
          <FileDownloadOutlinedIcon sx={{ fontSize: 15 }} />
          {t("rightPanel.download", "Télécharger")}
        </Box>
      </Box>

      {/* ── Tab bar (segmented pill) ── */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: isDark ? alpha('#fff', 0.05) : alpha('#000', 0.04),
        borderRadius: '11px',
        p: '4px',
        mb: 1.5,
        gap: '2px',
        flexShrink: 0,
      }}>
        {TABS.map((tab) => {
          const isActive = activeView === tab.id;
          const isLocked = isTabLocked(tab.id);
          const label = t(tab.labelKey, TAB_LABELS[tab.labelKey] || tab.id);
          const tooltipLabel = isLocked
            ? `${label} — ${t("rightPanel.lockedTooltip", "Available with a Lawis subscription")}`
            : label;

          return (
            <Tooltip key={tab.id} title={tooltipLabel} placement="bottom" arrow>
              <IconButton
                data-tour={`tab-${tab.id}`}
                onClick={() => handleSetView(tab.id)}
                sx={{
                  flex: 1,
                  borderRadius: '7px',
                  padding: '6px',
                  minWidth: 0,
                  opacity: isLocked && !isActive ? 0.4 : 1,
                  backgroundColor: isActive
                    ? isDark ? alpha('#fff', 0.13) : '#fff'
                    : 'transparent',
                  color: isActive
                    ? theme.palette.text.primary
                    : alpha(theme.palette.text.secondary, 0.7),
                  boxShadow: isActive && !isDark
                    ? '0 1px 3px rgba(0,0,0,0.12)'
                    : 'none',
                  transition: 'all 0.16s ease',
                  '&:hover': {
                    backgroundColor: isActive
                      ? isDark ? alpha('#fff', 0.15) : '#fff'
                      : isDark ? alpha('#fff', 0.06) : alpha('#000', 0.04),
                    color: theme.palette.text.primary,
                    opacity: 1,
                  },
                }}
              >
                {tab.icon}
              </IconButton>
            </Tooltip>
          );
        })}
      </Box>

      {/* ── Content area ── */}
      <Box data-tour="right-panel-content" sx={{
        flexGrow: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Show locked overlay for locked tabs */}
        {isTabLocked(activeView) ? (
          <LockedTabOverlay />
        ) : (
          <>
            {activeView === "chatBot" && (
              <Box sx={{ height: '100%', overflow: 'hidden' }}>
                <ChatBot contractId={contractId} userId={userId} isVisible />
              </Box>
            )}
            {activeView === "keyInfo" && (
              <Box sx={{ height: '100%', overflow: 'hidden' }}>
                <KeyInfo contractId={contractId} startKeyInfoExtraction={startKeyInfoExtraction} />
              </Box>
            )}
            {activeView === "riskAnalysis" && (
              <Box sx={{ height: '100%', overflow: 'hidden' }}>
                <RiskAnalysis contractId={contractId} startRiskAnalysis={startRiskAnalysis} />
              </Box>
            )}
            {activeView === "explanation" && (
              <Box sx={{ height: '100%', overflow: 'hidden' }}>
                <Explanation contractId={contractId} startClauseExtraction={startClauseExtraction} />
              </Box>
            )}
            {activeView === "suggestion" && (
              <Box sx={{ height: '100%', overflow: 'hidden' }}>
                <Suggestion contractId={contractId} startSuggestions={startSuggestions} />
              </Box>
            )}
            {activeView === "collaboration" && (
              <Box sx={{ height: '100%', overflow: 'hidden' }}>
                <Collaboration contractId={contractId} />
              </Box>
            )}
            {activeView === "signature" && (
              <Box sx={{ height: '100%', overflow: 'hidden' }}>
                <Signature contractId={contractId} />
              </Box>
            )}
            {activeView === "history" && (
              <Box sx={{ height: '100%', overflow: 'hidden' }}>
                <HistoryComponent />
              </Box>
            )}
          </>
        )}
      </Box>

      <DownloadDialog
        open={downloadDialogOpen}
        onClose={() => setDownloadDialogOpen(false)}
        contractId={contractId}
        onBeforeDownload={onBeforeDownload}
      />
    </Box>
  );
};

export default RightPanel;
